import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Receipt, Send, Users, Wallet, Package, BarChart3,
  Settings, LogOut, ShieldCheck, UserCircle2, ClipboardCheck, Store, Clock,
  SlidersHorizontal, UserCog, KeyRound, Contact, Activity, Link2,
  FlaskConical, Key, BookOpen, Globe, CreditCard, Database, ArrowUpRight,
  Menu as MenuIcon, TestTube2, TrendingUp, History, Zap, ShieldAlert,
  RotateCw, Coins, Sparkles, UserCheck, ChevronDown, ChevronRight, LayoutGrid, Building2, GraduationCap,
  Search,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/hooks/useFeatures";
import { encodeAdminPath } from "@/lib/adminRoutes";
import { isAdminPortal } from "@/lib/domain";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { WalletHeaderPill } from "@/components/WalletHeaderPill";
import VersionPill from "@/components/VersionPill";
import { api } from "@/lib/api";

// Icon string → Lucide component map
const ICON_MAP = {
  LayoutDashboard, Receipt, Send, Users, Wallet, Package, BarChart3,
  Settings, ShieldCheck, ClipboardCheck, Store, Clock, SlidersHorizontal,
  UserCog, KeyRound, Contact, Activity, Link2, FlaskConical, Key, BookOpen,
  Globe, CreditCard, Database, ArrowUpRight, TestTube2, TrendingUp, History,
  Zap, ShieldAlert, RotateCw, Coins, Sparkles, UserCheck, LayoutGrid, Building2, GraduationCap,
};

// ── Hardcoded fallbacks (used when API unavailable) ─────────────

const FALLBACK_PARTNER = [
  { to: "/dashboard", label: "Overview", icon: "LayoutDashboard" },
  { to: "/bbps", label: "BBPS", icon: "Receipt", featureKey: "mod_bbps" },
  { to: "/payouts", label: "Payouts", icon: "Send", featureKey: "mod_payouts" },
  { to: "/payouts/pending-collections", label: "Pending links", icon: "Clock", featureKey: "mod_payouts" },
  { to: "/beneficiaries", label: "Beneficiaries", icon: "Users", featureKey: "mod_beneficiaries" },
  { to: "/kyc-links", label: "KYC Links", icon: "Link2" },
  { to: "/customer-kyc", label: "Customer KYC", icon: "ShieldCheck" },
  { to: "/customer-wallet", label: "Customer Wallet", icon: "Wallet" },
  { to: "/wallet", label: "Wallet", icon: "Wallet", featureKey: "mod_wallet" },
  { to: "/payment-links", label: "Payment Links", icon: "CreditCard" },
  { to: "/transactions", label: "Transactions", icon: "Receipt", featureKey: "mod_transactions" },
  { to: "/reports", label: "Reports", icon: "BarChart3", featureKey: "mod_reports" },
  { to: "/packages", label: "Package", icon: "Package" },
];

const FALLBACK_ADMIN = [
  { to: "/admin/approvals", label: "Approvals", icon: "ClipboardCheck", featureKey: "admin_approvals" },
  { to: "/admin/all-txns", label: "All transactions", icon: "Activity", featureKey: "admin_all_txns" },
  { to: "/admin/beneficiary-approvals", label: "Beneficiary approvals", icon: "UserCheck", featureKey: "admin_beneficiary_approvals" },
  { to: "/admin/kyc", label: "KYC Queue", icon: "ShieldCheck", featureKey: "admin_kyc_queue" },
  { to: "/admin/retailers", label: "Retailers", icon: "Store", featureKey: "admin_retailers" },
  { to: "/admin/users", label: "Partners", icon: "Users", featureKey: "admin_partners" },
  { to: "/admin/customers", label: "Customers", icon: "Contact", featureKey: "admin_customers" },
  { to: "/admin/customer-kyc", label: "Customer KYC", icon: "ShieldCheck", featureKey: "admin_customer_kyc" },
  { to: "/admin/customer-wallet-rules", label: "Wallet rules", icon: "Wallet", featureKey: "admin_wallet_rules" },
  { to: "/admin/sub-admins", label: "Sub-admins", icon: "UserCog", featureKey: "admin_team_manage" },
  { to: "/admin/roles", label: "Roles", icon: "KeyRound", featureKey: "admin_team_manage" },
  { to: "/admin/packages", label: "Packages", icon: "Package", featureKey: "admin_packages" },
  { to: "/admin/feature-matrix", label: "Feature matrix", icon: "SlidersHorizontal", featureKey: "admin_team_manage" },
  { to: "/admin/integrations", label: "Integrations", icon: "Settings", featureKey: "admin_integrations" },
  { to: "/admin/payment-links", label: "Payment links", icon: "Link2", featureKey: "admin_payment_links" },
  { to: "/admin/outbound-payments", label: "Outbound payments", icon: "Wallet", featureKey: "admin_outbound_payments" },
  { to: "/admin/outbound-txns", label: "Outbound Txn Log", icon: "ArrowUpRight", featureKey: "admin_outbound_txns" },
  { to: "/admin/qa", label: "QA Center", icon: "TestTube2", featureKey: "admin_qa_api" },
  { to: "/admin/signup-report", label: "Signup funnel", icon: "TrendingUp", featureKey: "admin_signup_funnel" },
  { to: "/admin/activity-log", label: "Activity log", icon: "History", featureKey: "admin_activity_log" },
  { to: "/admin/beneficiary-library", label: "Beneficiary library", icon: "Database", featureKey: "admin_beneficiary_lib" },
  { to: "/admin/api-tester", label: "API tester", icon: "FlaskConical", featureKey: "admin_api_tester" },
  { to: "/admin/api-logs", label: "API call logs", icon: "Activity", featureKey: "admin_api_logs" },
  { to: "/admin/sdk-clients", label: "SDK clients", icon: "Key", featureKey: "admin_sdk_clients" },
  { to: "/admin/platform", label: "Platform & API docs", icon: "BookOpen", featureKey: "admin_platform_docs" },
  { to: "/admin/webhook-events", label: "Webhook events", icon: "Activity", featureKey: "admin_webhooks" },
  { to: "/admin/payment-modes", label: "Pay mode whitelist", icon: "ShieldCheck", featureKey: "admin_payment_modes" },
  { to: "/admin/wallet-txns", label: "Wallet transactions", icon: "Wallet", featureKey: "admin_wallet_txns" },
  { to: "/admin/stuck-debits", label: "Stuck wallet debits", icon: "ShieldAlert", featureKey: "admin_stuck_debits" },
  { to: "/admin/reconciliation", label: "Reconciliation jobber", icon: "RotateCw", featureKey: "admin_reconciliation" },
  { to: "/admin/topup-fees", label: "Top-up fees (PG MDR)", icon: "Coins", featureKey: "admin_topup_fees" },
  { to: "/admin/landing-services", label: "Landing services & ads", icon: "Sparkles", featureKey: "admin_landing_services" },
  { to: "/admin/analytics", label: "Analytics & Pixel", icon: "BarChart3", featureKey: "admin_analytics" },
  { to: "/admin/channels", label: "Business Channels", icon: "Building2", featureKey: "admin_channels" },
  { to: "/admin/system-test", label: "System Connection Test", icon: "Zap", featureKey: "admin_system_test" },
  { to: "/admin/domains", label: "Domains", icon: "Globe", featureKey: "admin_domains" },
  { to: "/admin/menu-config", label: "Menu layout", icon: "LayoutGrid", featureKey: "admin_menu_config" },
];

const FALLBACK_APPROVER = [
  { to: "/admin/approvals", label: "My queue", icon: "ClipboardCheck" },
  { to: "/admin/all-txns", label: "All transactions", icon: "Activity" },
  { to: "/admin/beneficiary-approvals", label: "Beneficiary approvals", icon: "UserCheck" },
];

const FALLBACK_USER = [
  { to: "/become-retailer", label: "Become a Retailer", icon: "Store", featureKey: "mod_retailer_apply" },
  { to: "/wallet", label: "Wallet", icon: "Wallet", featureKey: "mod_wallet" },
];

const FALLBACK_CHANNEL_PARTNER = [
  { to: "/channel/dashboard", label: "Channel Overview", icon: "LayoutDashboard" },
  { to: "/channel/sub-partners", label: "Sub-Partners", icon: "Users" },
  { to: "/channel/transactions", label: "Channel Activity", icon: "Activity" },
  { to: "/channel/earnings", label: "Earnings", icon: "BarChart3" },
  { to: "/bbps", label: "BBPS", icon: "Receipt" },
  { to: "/payouts", label: "Payouts", icon: "Send" },
  { to: "/wallet", label: "Wallet", icon: "Wallet" },
  { to: "/transactions", label: "My Transactions", icon: "Receipt" },
];

// ── Collapse persistence ────────────────────────────────────────

const COLLAPSE_KEY = "flexpay_menu_collapsed";

function loadCollapsed() {
  try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "{}"); } catch { return {}; }
}

function saveCollapsed(obj) {
  try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(obj)); } catch { /* localStorage disabled */ }
}

// ── Flatten grouped config into NavLink-ready items ─────────────

function flattenToItems(menuConfig) {
  const items = [];
  for (const entry of menuConfig) {
    if (entry.type === "group" && Array.isArray(entry.items)) {
      for (const item of entry.items) items.push(item);
    } else if (entry.to) {
      items.push(entry);
    }
  }
  return items;
}

// ── Components ──────────────────────────────────────────────────

function NavItem({ item, onItemClick, isAdmin }) {
  const Icon = ICON_MAP[item.icon] || Zap;
  const to = isAdmin && item.to?.startsWith("/admin") ? encodeAdminPath(item.to) : item.to;
  return (
    <NavLink
      to={to}
      onClick={onItemClick}
      data-testid={`nav-${item.label?.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) => [
        "flex items-center justify-between gap-3 px-3 py-2 rounded-md text-sm transition-colors",
        isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-accent",
      ].join(" ")}
    >
      <span className="flex items-center gap-3">
        <Icon className="h-4 w-4" strokeWidth={1.5} />
        {item.label}
      </span>
    </NavLink>
  );
}

function NavGroup({ group, isAdmin, isEnabled, onItemClick, collapsed, onToggle, forceOpen }) {
  const visibleItems = (group.items || []).filter(
    (it) => !it.featureKey || isEnabled(it.featureKey),
  );
  if (visibleItems.length === 0) return null;

  const open = forceOpen || !collapsed;
  return (
    <div data-testid={`nav-group-${group.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
        data-testid={`nav-group-toggle-${group.id}`}
      >
        {group.label}
        {open ? (
          <ChevronDown className="h-3 w-3" strokeWidth={2} />
        ) : (
          <ChevronRight className="h-3 w-3" strokeWidth={2} />
        )}
      </button>
      {open && (
        <div className="space-y-0.5 mb-1.5">
          {visibleItems.map((item) => (
            <NavItem key={item.id || item.to} item={item} onItemClick={onItemClick} isAdmin={isAdmin} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Filter menu entries (groups + top-level items) against a search query.
 * - If a group's LABEL matches, all its children stay visible.
 * - Otherwise only children whose LABEL matches survive.
 * - Groups that end up empty are dropped entirely.
 * - Top-level items whose label doesn't match are dropped.
 * Empty query → returns entries unchanged.
 */
function filterEntries(entries, rawQuery) {
  const q = (rawQuery || "").trim().toLowerCase();
  if (!q) return entries;
  const matches = (label) => (label || "").toLowerCase().includes(q);
  const out = [];
  for (const entry of entries || []) {
    if (entry.type === "group" && Array.isArray(entry.items)) {
      if (matches(entry.label)) {
        out.push(entry);
        continue;
      }
      const items = entry.items.filter((it) => matches(it.label));
      if (items.length) out.push({ ...entry, items });
    } else if (matches(entry.label)) {
      out.push(entry);
    }
  }
  return out;
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { isEnabled } = useFeatures();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuConfig, setMenuConfig] = useState(null);
  const [menuLoaded, setMenuLoaded] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(loadCollapsed);
  // Sidebar search — filters nav labels live. Empty = show everything.
  const [navQuery, setNavQuery] = useState("");

  // Fetch menu config once
  useEffect(() => {
    api.get("/menu/config")
      .then((res) => { if (res.data) setMenuConfig(res.data); })
      .catch(() => {})
      .finally(() => setMenuLoaded(true));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const toggleGroup = (groupId) => {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      saveCollapsed(next);
      return next;
    });
  };

  // Determine role context
  const role = user?.role || "";
  const isCustomAdmin = role.startsWith("custom_admin_");
  const isCustomPartner = role.startsWith("custom_partner_");
  const isAdmin = role === "admin" || isCustomAdmin;
  const isApprover = role === "approver_all" || /^approver_l[123]$/.test(role);
  const isChannelPartner = role === "channel_partner";
  // Admin on partner portal → show partner view
  const onAdminPortal = isAdminPortal();
  const adminAsPartner = isAdmin && !onAdminPortal;

  let roleLabel = "Partner";
  let homePath = "/dashboard";
  if (isAdmin && !adminAsPartner) {
    roleLabel = role === "admin" ? "Admin" : "Sub-admin";
    homePath = encodeAdminPath("/admin");
  } else if (adminAsPartner) {
    roleLabel = "Admin (Partner view)";
    homePath = "/dashboard";
  } else if (role === "approver_all") {
    roleLabel = "Approver (All Levels)";
    homePath = encodeAdminPath("/admin/approvals");
  } else if (/^approver_l[123]$/.test(role)) {
    roleLabel = `Approver L${role.split("_l")[1]}`;
    homePath = encodeAdminPath("/admin/approvals");
  } else if (isChannelPartner) {
    roleLabel = "Channel Partner";
    homePath = "/channel/dashboard";
  } else if (role === "user") {
    roleLabel = "Hitch Zone User";
    homePath = "/become-retailer";
  } else if (isCustomPartner) {
    roleLabel = "Partner (custom)";
  }

  // Resolve menu: API config → fallback (only after API has responded)
  // Admin on partner portal → use partner menu
  let menuEntries;
  // When an admin is browsing the partner portal we ALSO show every admin
  // menu group at the bottom so admin-only surfaces (Marketing & Analytics,
  // Approvals, etc.) remain one click away without a domain switch.
  let extraAdminEntries = null;
  if (isApprover) {
    menuEntries = null;
  } else if (isChannelPartner) {
    menuEntries = null; // flat fallback with channel + partner nav
  } else if (adminAsPartner) {
    // Admin on partner portal → show partner menu
    if (menuLoaded && menuConfig?.partner) {
      menuEntries = menuConfig.partner;
    } else if (!menuLoaded) {
      menuEntries = "loading";
    }
    // Append the admin menu groups so admin features stay reachable.
    if (menuLoaded && menuConfig?.admin) {
      extraAdminEntries = menuConfig.admin;
    }
  } else if (menuLoaded && isAdmin && menuConfig?.admin) {
    menuEntries = menuConfig.admin;
  } else if (menuLoaded && !isAdmin && !isApprover && !isChannelPartner && menuConfig?.partner) {
    menuEntries = menuConfig.partner;
  } else if (!menuLoaded && !isApprover && !isChannelPartner) {
    menuEntries = "loading";
  }

  // Build flat fallback for roles without grouped config
  let flatNav = null;
  if (!menuEntries || menuEntries === "loading") {
    if (adminAsPartner) flatNav = FALLBACK_PARTNER;
    else if (isAdmin) flatNav = FALLBACK_ADMIN;
    else if (isChannelPartner) flatNav = FALLBACK_CHANNEL_PARTNER;
    else if (isApprover) flatNav = FALLBACK_APPROVER;
    else if (role === "user") flatNav = FALLBACK_USER;
    else flatNav = FALLBACK_PARTNER;
  }

  const renderNav = (onItemClick) => {
    const searching = !!navQuery.trim();
    const filteredEntries = searching && Array.isArray(menuEntries)
      ? filterEntries(menuEntries, navQuery)
      : menuEntries;
    const filteredAdminEntries = searching && Array.isArray(extraAdminEntries)
      ? filterEntries(extraAdminEntries, navQuery)
      : extraAdminEntries;
    const filteredFlat = searching && Array.isArray(flatNav)
      ? filterEntries(flatNav, navQuery)
      : flatNav;
    const noResults =
      searching &&
      (!filteredEntries || (Array.isArray(filteredEntries) && filteredEntries.length === 0)) &&
      (!filteredAdminEntries || filteredAdminEntries.length === 0) &&
      (!filteredFlat || filteredFlat.length === 0);

    return (
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
      <div className="overline px-3 py-2">{roleLabel}</div>

      {/* Sidebar search — filters nav labels live. Groups auto-expand when
          a query is active so results are visible without extra clicks. */}
      <div className="px-3 pb-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
          <input
            type="search"
            value={navQuery}
            onChange={(e) => setNavQuery(e.target.value)}
            placeholder="Search…"
            className="w-full h-8 pl-8 pr-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="sidebar-search"
            aria-label="Search navigation"
          />
        </div>
      </div>

      {menuEntries === "loading" ? (
        // Brief skeleton while config loads — avoids flat-list flash
        Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-8 rounded-md bg-muted/40 animate-pulse mx-3 mb-1" />
        ))
      ) : noResults ? (
        <div className="px-3 py-6 text-center text-xs text-muted-foreground" data-testid="sidebar-search-empty">
          No pages match “{navQuery.trim()}”
        </div>
      ) : filteredEntries && filteredEntries !== "loading" ? (
        <>
          {filteredEntries.map((entry) => {
            if (entry.type === "group") {
              return (
                <NavGroup
                  key={entry.id}
                  group={entry}
                  isAdmin={isAdmin}
                  isEnabled={isEnabled}
                  onItemClick={onItemClick}
                  collapsed={!!collapsedGroups[entry.id]}
                  onToggle={() => toggleGroup(entry.id)}
                  forceOpen={searching}
                />
              );
            }
            // Top-level ungrouped item
            if (entry.featureKey && !isEnabled(entry.featureKey)) return null;
            return <NavItem key={entry.id || entry.to} item={entry} onItemClick={onItemClick} isAdmin={isAdmin} />;
          })}
          {/* Admin-only entries appended when an admin browses the partner
              portal. Visually separated so it's obvious these go to the admin
              console (URLs are auto-encoded by NavItem when they start with /admin). */}
          {filteredAdminEntries && filteredAdminEntries.length > 0 && (
            <>
              <div className="mt-4 mb-1 mx-3 border-t border-border" />
              <div className="overline px-3 py-2 text-[10px] opacity-70" data-testid="admin-console-section">
                Admin Console
              </div>
              {filteredAdminEntries.map((entry) => {
                if (entry.type === "group") {
                  return (
                    <NavGroup
                      key={`adm-${entry.id}`}
                      group={entry}
                      isAdmin={isAdmin}
                      isEnabled={isEnabled}
                      onItemClick={onItemClick}
                      collapsed={!!collapsedGroups[`adm-${entry.id}`]}
                      onToggle={() => toggleGroup(`adm-${entry.id}`)}
                      forceOpen={searching}
                    />
                  );
                }
                if (entry.featureKey && !isEnabled(entry.featureKey)) return null;
                return <NavItem key={`adm-${entry.id || entry.to}`} item={entry} onItemClick={onItemClick} isAdmin />;
              })}
            </>
          )}
        </>
      ) : (
        // Flat fallback (approvers, user role, or API unavailable)
        (filteredFlat || [])
          .filter((n) => !n.featureKey || isEnabled(n.featureKey))
          .map((n) => {
            const to = n.to?.startsWith("/admin") ? encodeAdminPath(n.to) : n.to;
            const Icon = ICON_MAP[n.icon] || Zap;
            return (
              <NavLink
                key={to}
                to={to}
                onClick={onItemClick}
                data-testid={`nav-${n.label?.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => [
                  "flex items-center justify-between gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                  isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-accent",
                ].join(" ")}
              >
                <span className="flex items-center gap-3">
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                  {n.label}
                </span>
              </NavLink>
            );
          })
      )}
    </nav>
    );
  };
  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-64 flex-col border-r border-border bg-card">
        <div className="h-16 flex items-center px-6 border-b border-border">
          <Link to={homePath} data-testid="brand-mark" className="inline-flex items-center gap-2 font-display font-extrabold text-lg">
            <span className="grid place-items-center h-8 w-8 rounded-md bg-primary text-primary-foreground font-mono text-sm">F</span>
            FlexPay
          </Link>
        </div>
        {renderNav()}
        <div className="p-4 border-t border-border text-xs text-muted-foreground">v0.1 · Phase 1</div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-white/70 backdrop-blur-xl sticky top-0 z-30 flex items-center px-4 lg:px-8 justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button type="button" data-testid="open-mobile-nav" className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-border hover:bg-accent" aria-label="Open navigation">
                  <MenuIcon className="h-5 w-5" strokeWidth={1.5} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-card">
                <SheetHeader className="h-16 px-6 border-b border-border flex flex-row items-center">
                  <SheetTitle className="font-display font-extrabold text-lg flex items-center gap-2">
                    <span className="grid place-items-center h-8 w-8 rounded-md bg-primary text-primary-foreground font-mono text-sm">F</span>
                    FlexPay
                  </SheetTitle>
                </SheetHeader>
                {renderNav(() => setMobileOpen(false))}
                <div className="p-4 border-t border-border text-xs text-muted-foreground">v0.1 · Phase 1</div>
              </SheetContent>
            </Sheet>
            <Link to={homePath} data-testid="brand-mark-mobile" className="lg:hidden inline-flex items-center gap-2 font-display font-bold">
              <span className="grid place-items-center h-7 w-7 rounded-md bg-primary text-primary-foreground font-mono text-xs">F</span>
              <span className="text-sm">FlexPay</span>
            </Link>
            <span className="overline hidden md:inline truncate">
              {user?.role === "admin" ? "Admin Console" : "Partner Console"}
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <VersionPill />
            {user?.role !== "admin" && !user?.role?.startsWith("approver_") && <WalletHeaderPill />}
            {user?.role !== "admin" && (
              <Badge
                data-testid="kyc-status-badge"
                variant={user?.kyc_status === "verified" ? "default" : "outline"}
                className={[
                  user?.kyc_status === "verified" ? "bg-success text-success-foreground hover:bg-success" : "",
                  "hidden sm:inline-flex",
                ].join(" ")}
              >
                KYC: {user?.kyc_status}
              </Badge>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 hover:bg-accent transition-colors">
                <UserCircle2 className="h-5 w-5" strokeWidth={1.5} />
                <span className="hidden sm:inline text-sm font-medium">{user?.full_name || "Account"}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="font-medium">{user?.full_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user?.role !== "admin" && (
                  <DropdownMenuItem asChild>
                    <Link to="/kyc" data-testid="menu-kyc">KYC Details</Link>
                  </DropdownMenuItem>
                )}
                {user?.role !== "admin" && user?.retailer_status !== "verified" && (
                  <DropdownMenuItem asChild>
                    <Link to="/become-retailer" data-testid="menu-business-profile">
                      Business profile <span className="ml-auto text-[10px] text-muted-foreground">optional</span>
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-logout" onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-3 sm:p-4 lg:p-8 min-w-0">{children}</main>
      </div>
    </div>
  );
}
